import { z } from 'zod';
import { IsoDateTime } from './common.js';
import { PatientBannerSchema } from './patient.js';

/**
 * Active patient context for one user session. Exactly zero or one per session.
 * Stored with a TTL; changing it invalidates every pending action for the previous patient.
 */
export const PatientContextSchema = z.object({
  tenantId: z.string(),
  userId: z.string(),
  sessionId: z.string(),
  patient: PatientBannerSchema,
  activeEncounterId: z.string().optional(),
  /** How identity was verified when the context was set. */
  verifiedBy: z.array(z.enum(['MRN', 'NAME', 'BIRTH_DATE', 'NATIONAL_ID', 'USER_CONFIRMATION'])),
  setAt: IsoDateTime,
  expiresAt: IsoDateTime,
  version: z.number().int().default(1),
});
export type PatientContext = z.infer<typeof PatientContextSchema>;

export const PendingActionKindSchema = z.enum([
  'ORDER',
  'PRESCRIPTION',
  'APPOINTMENT',
  'APPOINTMENT_CANCEL',
  'APPOINTMENT_RESCHEDULE',
  'CLINICAL_NOTE',
  'REGISTRATION',
  'CONTEXT_SWITCH',
  'ENCOUNTER_UPDATE',
  'RECORD_ENTRY',
]);
export type PendingActionKind = z.infer<typeof PendingActionKindSchema>;

export const PendingActionStatusSchema = z.enum(['PENDING', 'CONFIRMED', 'REJECTED', 'EXPIRED', 'INVALIDATED']);

/**
 * A draft awaiting explicit confirmation. The payload is the exact input the deterministic
 * executor will receive; the LLM cannot alter it after creation. Confirmation is idempotent via idempotencyKey.
 */
export const PendingActionSchema = z.object({
  tenantId: z.string(),
  userId: z.string(),
  sessionId: z.string(),
  pendingActionId: z.string(),
  kind: PendingActionKindSchema,
  /** Null for actions that are not patient-specific (e.g. REGISTRATION). */
  patientId: z.string().nullable(),
  summary: z.string().describe('Human-readable description shown on the confirmation card'),
  payload: z.record(z.string(), z.unknown()),
  cdsFindingIds: z.array(z.string()).default([]),
  /** Finding ids the clinician must acknowledge before confirmation (critical findings). */
  requiresAcknowledgement: z.array(z.string()).default([]),
  status: PendingActionStatusSchema,
  idempotencyKey: z.string(),
  /** Set when confirmed: the id of the executed record so replays return the same result. */
  resultRef: z.object({ type: z.string(), id: z.string() }).optional(),
  createdAt: IsoDateTime,
  expiresAt: IsoDateTime,
  resolvedAt: IsoDateTime.optional(),
});
export type PendingAction = z.infer<typeof PendingActionSchema>;

/** Short conversation memory kept server-side so the LLM gets consistent history across Lambda invocations. */
export const ConversationTurnSchema = z.object({
  role: z.enum(['user', 'assistant']),
  text: z.string(),
  at: IsoDateTime,
  inputMode: z.enum(['text', 'voice']).optional(),
  toolCalls: z.array(z.object({ name: z.string(), ok: z.boolean() })).optional(),
});
export type ConversationTurn = z.infer<typeof ConversationTurnSchema>;

export const ConversationSchema = z.object({
  tenantId: z.string(),
  userId: z.string(),
  sessionId: z.string(),
  conversationId: z.string(),
  turns: z.array(ConversationTurnSchema).max(40),
  updatedAt: IsoDateTime,
  expiresAt: IsoDateTime,
});
export type Conversation = z.infer<typeof ConversationSchema>;
